import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Shield, ChevronDown, ChevronRight, Search, Save, Loader2, RotateCcw, CheckCircle2, MinusCircle, Circle } from 'lucide-react';
import { toast } from 'sonner';

const statusOf = (sel, total) => (sel === 0 ? 'none' : sel === total ? 'full' : 'partial');
const STATUS_STYLE = {
  full: { icon: CheckCircle2, cls: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  partial: { icon: MinusCircle, cls: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  none: { icon: Circle, cls: 'text-muted-foreground/60', badge: 'bg-muted text-muted-foreground' },
};

export const PermissoesModal = ({ open, onOpenChange, usuario, onSaved }) => {
  const { api } = useAuth();
  const [catalogo, setCatalogo] = useState(null);
  const [info, setInfo] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState({});
  const [busca, setBusca] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmarCritico, setConfirmarCritico] = useState(false);

  useEffect(() => {
    if (!open || !usuario) return;
    setLoading(true); setBusca(''); setConfirmarCritico(false);
    Promise.all([api.get('/permissoes/disponiveis'), api.get(`/usuarios/${usuario.id}/permissoes`)])
      .then(([c, u]) => {
        setCatalogo(c.data); setInfo(u.data); setSelected(new Set(u.data.permissoes || []));
        setExpanded(Object.fromEntries(c.data.modulos.map((m) => [m.key, false])));
      })
      .catch(() => toast.error('Erro ao carregar permissões'))
      .finally(() => setLoading(false));
  }, [open, usuario, api]);

  const modulos = catalogo?.modulos || [];
  const implica = catalogo?.implica || {};
  const total = catalogo?.permissoes?.length || 0;

  const filtered = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return modulos;
    return modulos
      .map((m) => ({ ...m, permissoes: m.permissoes.filter((p) => p.label.toLowerCase().includes(q) || p.key.includes(q) || m.label.toLowerCase().includes(q)) }))
      .filter((m) => m.permissoes.length > 0);
  }, [modulos, busca]);

  const withDeps = (set) => {
    const s = new Set(set);
    let changed = true;
    while (changed) { changed = false; s.forEach((p) => { const d = implica[p]; if (d && !s.has(d)) { s.add(d); changed = true; } }); }
    return s;
  };

  const toggle = (key) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(key)) {
        s.delete(key);
        Object.entries(implica).forEach(([p, dep]) => { if (dep === key) s.delete(p); });
      } else { s.add(key); }
      return withDeps(s);
    });
  };
  const setModule = (m, on) => setSelected((prev) => {
    const s = new Set(prev); m.permissoes.forEach((p) => (on ? s.add(p.key) : s.delete(p.key))); return withDeps(s);
  });
  const setAll = (on) => setSelected(on ? new Set(catalogo.permissoes) : new Set());
  const setAllExpanded = (on) => setExpanded(Object.fromEntries(modulos.map((m) => [m.key, on])));
  const resetTemplate = () => setSelected(new Set(info?.template || []));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/usuarios/${usuario.id}/permissoes`, { permissoes: [...selected], confirmar_critico: confirmarCritico });
      toast.success(`Permissões salvas (+${r.data.adicionadas.length} / -${r.data.removidas.length})`);
      onSaved?.(r.data.permissoes); onOpenChange(false);
    } catch (e) {
      if (e.response?.status === 409) { setConfirmarCritico(true); toast.warning(e.response.data.detail + '. Clique em Salvar novamente para confirmar.'); }
      else toast.error(e.response?.data?.detail || 'Erro ao salvar permissões');
    } finally { setSaving(false); }
  };

  const isMasterTarget = info?.is_master;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[90vh] flex flex-col p-0 gap-0" data-testid="permissoes-modal">
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2"><Shield className="h-5 w-5" /> Gerenciar Permissões</DialogTitle>
          <DialogDescription>
            {usuario && <span>Configurar permissões para <strong>{usuario.nome}</strong> ({usuario.email}) · perfil <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium">{usuario.perfil}</span>{info?.personalizadas && <span className="ml-1 inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium" data-testid="perm-badge-personalizadas">personalizadas</span>}</span>}
          </DialogDescription>
          {!isMasterTarget && (
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar permissão..." className="pl-8 h-9" data-testid="perm-busca" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setAll(true)} data-testid="perm-selecionar-todas">Selecionar todas</Button>
              <Button variant="outline" size="sm" onClick={() => setAll(false)} data-testid="perm-limpar-todas">Limpar todas</Button>
              <Button variant="ghost" size="sm" onClick={() => setAllExpanded(true)} data-testid="perm-expandir">Expandir todos</Button>
              <Button variant="ghost" size="sm" onClick={() => setAllExpanded(false)} data-testid="perm-recolher">Recolher todos</Button>
              <Button variant="ghost" size="sm" onClick={resetTemplate} title="Voltar ao padrão do perfil" data-testid="perm-template"><RotateCcw className="h-3.5 w-3.5 mr-1" />Padrão do perfil</Button>
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {loading && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>}
          {!loading && isMasterTarget && (
            <div className="rounded-lg border p-4 text-sm bg-muted/40" data-testid="perm-master-aviso">Master Admin possui acesso integral ao sistema. As permissões não são editáveis para este perfil.</div>
          )}
          {!loading && !isMasterTarget && filtered.map((m) => {
            const sel = m.permissoes.filter((p) => selected.has(p.key)).length;
            const totalM = modulos.find((x) => x.key === m.key)?.permissoes.length || m.permissoes.length;
            const selM = modulos.find((x) => x.key === m.key)?.permissoes.filter((p) => selected.has(p.key)).length ?? sel;
            const st = STATUS_STYLE[statusOf(selM, totalM)];
            const isOpen = expanded[m.key] || !!busca;
            return (
              <div key={m.key} className={`rounded-lg border transition-colors ${st.badge.includes('emerald') ? 'border-emerald-300/60' : st.badge.includes('amber') ? 'border-amber-300/60' : 'border-border'}`} data-testid={`perm-modulo-${m.key}`}>
                <div className="flex items-center gap-2 px-3 py-2.5 hover:bg-muted/50 rounded-t-lg">
                  <button type="button" onClick={() => setExpanded((e) => ({ ...e, [m.key]: !e[m.key] }))} className="flex-1 flex items-center gap-2 text-left" data-testid={`perm-modulo-toggle-${m.key}`}>
                    {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    <st.icon className={`h-4 w-4 ${st.cls}`} />
                    <span className="font-medium text-sm flex-1">{m.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${st.badge}`} data-testid={`perm-modulo-contador-${m.key}`}>{selM}/{totalM}</span>
                  </button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setModule(m, true)} data-testid={`perm-modulo-todas-${m.key}`}>Todas</Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setModule(m, false)} data-testid={`perm-modulo-limpar-${m.key}`}>Limpar</Button>
                </div>
                {isOpen && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 px-3 pb-3">
                    {m.permissoes.map((p) => {
                      const on = selected.has(p.key);
                      return (
                        <label key={p.key} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors ${on ? 'bg-primary/5 border-primary/40' : 'bg-background border-border hover:bg-muted/40'}`} data-testid={`perm-item-${p.key}`}>
                          <Checkbox checked={on} onCheckedChange={() => toggle(p.key)} data-testid={`perm-check-${p.key}`} />
                          <span className={on ? 'font-medium' : 'text-muted-foreground'}>{p.label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="px-6 py-3 border-t shrink-0 flex-row items-center justify-between sm:justify-between">
          <span className="text-sm" data-testid="perm-contador-total"><strong>{selected.size}</strong> de <strong>{total}</strong> permissões selecionadas</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="perm-cancelar">Cancelar</Button>
            {!isMasterTarget && (
              <Button onClick={save} disabled={saving || loading} data-testid="perm-salvar">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {confirmarCritico ? 'Confirmar e Salvar' : 'Salvar Permissões'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
